/**
 * Text sanitization utilities for handling unprintable characters
 * that can cause issues in PDF export and other text processing operations.
 */

const DELETE_CHAR = 0x7f;
const BYTE_ORDER_MARK = 0xfeff;
const ZERO_WIDTH_SPACE_START = 0x200b;
const ZERO_WIDTH_SPACE_END = 0x200d;
const PRINTABLE_CHAR_START = 32;
const TAB_CHAR = 9;
const LINE_FEED_CHAR = 10;
const CARRIAGE_RETURN_CHAR = 13;
const ALLOWED_WHITE_SPACES = [TAB_CHAR, LINE_FEED_CHAR, CARRIAGE_RETURN_CHAR];

const NULL_TO_BACKSPACE_START = 0x00;
const NULL_TO_BACKSPACE_END = 0x08;
const VERTICAL_TAB = 0x0b;
const FORM_FEED = 0x0c;
const SHIFT_OUT_TO_UNIT_SEPARATOR_START = 0x0e;
const SHIFT_OUT_TO_UNIT_SEPARATOR_END = 0x1f;
const HEX_PAD_LENGTH = 2;
const HEX_PAD_CHAR = "0";

/**
 * Sanitizes text by removing or replacing unprintable characters that can cause
 * issues in PDF generation and other text processing operations.
 *
 * This function removes:
 * - Control characters (0x00-0x1F) except for tab (0x09), line feed (0x0A), and carriage return (0x0D)
 * - Delete character (0x7F)
 * - Other problematic Unicode characters that can cause PDF export failures
 *
 * @param text - The input text that may contain unprintable characters
 * @returns Sanitized text with unprintable characters removed
 */
export function sanitizeText(text: string): string {
  if (!text) {
    return "";
  }

  // Remove control characters except tab, line feed, and carriage return
  // Using character filtering to avoid ESLint control-regex warning
  let result = "";
  for (let i = 0; i < text.length; i++) {
    const charCode = text.charCodeAt(i);

    // Check if character is allowed
    const isPrintableChar =
      charCode >= PRINTABLE_CHAR_START && charCode !== DELETE_CHAR;
    const isAllowedWhitespace = ALLOWED_WHITE_SPACES.includes(charCode);

    if (isPrintableChar || isAllowedWhitespace) {
      // Skip problematic Unicode characters
      const isByteOrderMark = charCode === BYTE_ORDER_MARK;
      const isZeroWidthChar =
        charCode >= ZERO_WIDTH_SPACE_START && charCode <= ZERO_WIDTH_SPACE_END;

      if (!isByteOrderMark && !isZeroWidthChar) {
        result += text[i];
      }
    }
  }

  return result;
}

/**
 * Character type classification for unprintable character detection
 */
interface CharacterClassification {
  isUnprintable: boolean;
  description: string;
}

/**
 * Classifies a character code and returns its type information
 */
function classifyCharacter(charCode: number): CharacterClassification {
  if (
    NULL_TO_BACKSPACE_START <= charCode &&
    charCode <= NULL_TO_BACKSPACE_END
  ) {
    return { isUnprintable: true, description: "Control character (NULL-BS)" };
  }

  if (VERTICAL_TAB === charCode) {
    return { isUnprintable: true, description: "Vertical tab" };
  }

  if (FORM_FEED === charCode) {
    return { isUnprintable: true, description: "Form feed" };
  }

  if (
    SHIFT_OUT_TO_UNIT_SEPARATOR_START <= charCode &&
    charCode <= SHIFT_OUT_TO_UNIT_SEPARATOR_END
  ) {
    return { isUnprintable: true, description: "Control character (SO-US)" };
  }

  if (DELETE_CHAR === charCode) {
    return { isUnprintable: true, description: "Delete character" };
  }

  if (BYTE_ORDER_MARK === charCode) {
    return { isUnprintable: true, description: "Byte Order Mark (BOM)" };
  }

  if (ZERO_WIDTH_SPACE_START <= charCode && charCode <= ZERO_WIDTH_SPACE_END) {
    return { isUnprintable: true, description: "Zero-width character" };
  }

  return { isUnprintable: false, description: "" };
}

/**
 * Creates character information object for unprintable characters
 */
function createCharacterInfo(
  index: number,
  char: string,
  charCode: number,
  description: string,
): {
  index: number;
  char: string;
  charCode: number;
  hex: string;
  description: string;
} {
  return {
    index,
    char,
    charCode,
    hex: charCode.toString(16).padStart(HEX_PAD_LENGTH, HEX_PAD_CHAR),
    description,
  };
}

/**
 * Gets information about unprintable characters found in the text.
 * Useful for debugging and logging purposes.
 *
 * @param text - The text to analyze
 * @returns Array of objects containing information about each unprintable character found
 */
export function getUnprintableCharacterInfo(text: string): Array<{
  index: number;
  char: string;
  charCode: number;
  hex: string;
  description: string;
}> {
  if (!text) {
    return [];
  }

  const unprintableChars: Array<{
    index: number;
    char: string;
    charCode: number;
    hex: string;
    description: string;
  }> = [];

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const charCode = char.charCodeAt(0);
    const classification = classifyCharacter(charCode);

    if (classification.isUnprintable) {
      unprintableChars.push(
        createCharacterInfo(i, char, charCode, classification.description),
      );
    }
  }

  return unprintableChars;
}
